import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getFotoDaRegra,
  FotoDaRegra,
  BoletoDoDiff,
  MotivoBoleto,
} from '../../../api/comissionamentosFotoApi';
// Tudo compartilhado: o modal com tabela paginada dos dashboards, a base visual dos cartões
// (`do-kpi*`), o selo e as faixas de erro/atenção. Nenhuma classe nova.
// ⚠️ A primeira versão desta entrega recriou casco, cabeçalho, abas, cartões e tabela num CSS
// próprio de ~200 linhas — contra a diretriz de reaproveitamento (24/07/2026).
import DetalheTabelaModal from '../../../shared/ui/DetalheTabelaModal';
import type { ColunaTabela } from '../../../shared/ui/TabelaPaginada';
import '../../../shared/ui/dashboard-base.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * A fotografia de uma regra num período, ao lado do estado atual (comissionamentos/at-04).
 *
 * É o drill-down do aviso: mostra quantos boletos compunham o cálculo quando a regra foi
 * criada, quantos valem hoje, e **quais** saíram ou entraram — com o motivo de cada um.
 *
 * 🔴 "Não há fotografia" NÃO é "nada mudou". Competência anterior a esta entrega não foi
 * fotografada, e o modal diz isso por extenso em vez de mostrar uma lista vazia — que seria
 * lida como "conferi e está tudo certo" sobre um pagamento que ninguém registrou.
 *
 * ⚠️ O motivo é APARENTE, derivado do que está gravado (situação do boleto na origem, regra
 * excluída, parâmetro diferente do que está na foto). Quando não dá para saber, o modal diz
 * que não foi possível identificar e manda conferir o cadastro — nunca inventa uma causa
 * plausível (regra de 11/09/2026).
 */

interface Props {
  regraId: string;
  periodo: string;
  /** Só para o cabeçalho — a observação da regra, como a linha da tabela já mostra. */
  descricaoDaRegra?: string;
  onClose: () => void;
}

const brl = (v: string | number | null) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const data = (iso: string | null) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};

const ROTULO_MOTIVO: Record<Exclude<MotivoBoleto, null>, string> = {
  ESTORNADO: 'Pagamento desfeito na origem',
  CANCELADO: 'Boleto cancelado',
  FORA_DA_BASE: 'Boleto não está mais na base',
  REGRA_EXCLUIDA: 'A regra foi excluída',
  REGRA_ALTERADA: 'A regra foi alterada depois da fotografia',
  PAGO_DEPOIS_DA_FOTO: 'Pagamento entrou depois da fotografia',
  SEM_MOTIVO_APARENTE: 'Motivo não identificado — confira o cadastro do veículo',
};

const ROTULO_SITUACAO: Record<BoletoDoDiff['situacao'], string> = {
  MANTIDO: 'Mantido',
  SAIU: 'Saiu',
  ENTROU: 'Entrou',
};

const COR_SITUACAO: Record<BoletoDoDiff['situacao'], string> = {
  MANTIDO: 'badge-cinza',
  SAIU: 'badge-vermelho',
  ENTROU: 'badge-azul',
};

const FotoDaRegraModal: React.FC<Props> = ({ regraId, periodo, descricaoDaRegra, onClose }) => {
  const [dados, setDados] = useState<FotoDaRegra | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState<'MUDARAM' | 'TODOS'>('MUDARAM');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await getFotoDaRegra(regraId, periodo));
      setErro(null);
    } catch (e) {
      // Nunca cair em "lista vazia": aqui vazio significaria "nada saiu".
      setDados(null);
      setErro(e instanceof Error ? e.message : 'Não foi possível carregar a fotografia.');
    } finally {
      setCarregando(false);
    }
  }, [regraId, periodo]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const temComparativo = Boolean(dados?.temFoto) && !erro && !carregando;

  const boletos: BoletoDoDiff[] = useMemo(
    () =>
      temComparativo
        ? (dados?.boletos ?? []).filter((b) => (aba === 'TODOS' ? true : b.situacao !== 'MANTIDO'))
        : [],
    [dados, aba, temComparativo],
  );

  const colunas: Array<ColunaTabela<BoletoDoDiff>> = [
    {
      titulo: 'Situação',
      largura: 110,
      texto: (b) => ROTULO_SITUACAO[b.situacao],
      render: (b) => (
        <span className={`cilia-badge ${COR_SITUACAO[b.situacao]}`}>
          {ROTULO_SITUACAO[b.situacao]}
        </span>
      ),
    },
    { titulo: 'Associado', largura: 220, texto: (b) => b.associado_nome ?? '-' },
    { titulo: 'Pagamento', largura: 110, texto: (b) => data(b.data_pagamento) },
    { titulo: 'Valor', largura: 110, texto: (b) => brl(b.valor_vencimento) },
    { titulo: 'Motivo', largura: 260, texto: (b) => (b.motivo ? ROTULO_MOTIVO[b.motivo] : '-') },
  ];

  /** O bloco acima da tabela: o comparativo, ou a explicação de por que não há um. */
  const cabecalho = (() => {
    if (carregando) {
      return (
        <p className="do-kpi-apoio" role="status" aria-busy="true">
          Carregando a fotografia...
        </p>
      );
    }
    if (erro) return <div className="erro-de-tela">{erro}</div>;
    if (dados?.disponivel === false) {
      // O `.sql` do módulo é passo manual: neste ambiente a fotografia ainda não existe.
      // Dizer isso é diferente de dizer que a competência não foi fotografada.
      return (
        <div className="aviso-de-tela">
          <strong className="aviso-de-tela-titulo">
            A fotografia ainda não está disponível neste ambiente.
          </strong>
          O registro do que compôs cada cálculo depende de uma configuração no banco que ainda não
          foi aplicada aqui. Acione a TI.
        </div>
      );
    }
    if (!dados?.temFoto) {
      return (
        <div className="aviso-de-tela">
          <strong className="aviso-de-tela-titulo">Esta competência não tem fotografia.</strong>
          O registro do que compôs o cálculo passou a ser guardado a partir desta versão do
          sistema, no momento em que a regra é criada. Para competências anteriores não há com o
          que comparar — hoje o cálculo alcança {dados?.agora.qtdEntradas ?? 0} boletos,{' '}
          {brl(dados?.agora.totalValorPago ?? 0)}.
        </div>
      );
    }
    return (
      <div className="do-kpis">
        <div className="do-kpi">
          <span className="do-kpi-rotulo">Quando a regra foi criada</span>
          <span className="do-kpi-valor">{dados.foto?.qtd_entradas ?? 0} boletos</span>
          <span className="do-kpi-apoio">
            {brl(dados.foto?.total_valor_pago ?? 0)} · repasse de{' '}
            {brl(dados.foto?.valor_calculado ?? 0)}
          </span>
          <span className="do-kpi-apoio">
            Fotografada em {data(dados.foto?.tirada_em ?? null)}
          </span>
        </div>
        <div className="do-kpi">
          <span className="do-kpi-rotulo">Hoje</span>
          <span className="do-kpi-valor">{dados.agora.qtdEntradas} boletos</span>
          <span className="do-kpi-apoio">{brl(dados.agora.totalValorPago)}</span>
          <span className="do-kpi-apoio">Recalculado agora</span>
        </div>
        <div className="do-kpi">
          <span className="do-kpi-rotulo">O que mudou</span>
          <span className="do-kpi-valor">
            {dados.resumo.sairam} {dados.resumo.sairam === 1 ? 'saiu' : 'saíram'}
          </span>
          <span className="do-kpi-apoio">
            {dados.resumo.entraram} {dados.resumo.entraram === 1 ? 'entrou' : 'entraram'} ·{' '}
            {dados.resumo.mantidos} continuam valendo
          </span>
        </div>
      </div>
    );
  })();

  return (
    <DetalheTabelaModal<BoletoDoDiff>
      titulo={`Fotografia da regra — ${periodo}`}
      apoio={descricaoDaRegra}
      cabecalho={cabecalho}
      // As abas só existem quando há o que filtrar: sem comparativo elas ofereceriam dois
      // recortes de uma lista vazia.
      abas={
        temComparativo
          ? [
              { id: 'MUDARAM', rotulo: 'Só o que mudou' },
              { id: 'TODOS', rotulo: 'Todos os boletos' },
            ]
          : undefined
      }
      abaAtiva={aba}
      onTrocarAba={(id) => setAba(id as 'MUDARAM' | 'TODOS')}
      itens={boletos}
      colunas={colunas}
      chave={(b) => b.recebimento_id}
      vazio={
        temComparativo
          ? 'Nenhum boleto entrou ou saiu desde a fotografia.'
          : 'Sem fotografia, não há o que comparar.'
      }
      onFechar={onClose}
    />
  );
};

export default FotoDaRegraModal;
